import { Component, Input, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { IReportData, IReportMetadata, IReportResponse, IReportRow, IReportValue, ReportDataService } from '../../../shared/report/report-data.service';
import * as _ from 'underscore';
import { Transition, UiOnParamsChanged } from '@uirouter/angular';
import { ReportFiltersUtil } from '../../../shared/report/report-filters-util.service';
import { TranslationService } from '../../../shared/translation/translation.service';
import { DeskOverviewDataService } from './desk-overview-data.service';
import { IObject } from '../../../../type_definitions/datatypes';
import * as moment from 'moment';
import { ScrollService } from '../../../shared/scroll/scroll.service';

const REPORT_ID:number = 264;

@Component({
  selector: 'desk-overview',
  template: `
    <div class="desks-overview">
      <h1>ACSL Organisation</h1>

      <h2 *ngIf="loading">
        <activity-indicator [useDefaultText]="true"></activity-indicator>
      </h2>

      <div *ngIf="!loading">
        <section class="desks" *ngFor="let desk of desks | keyvalue">
          
          <ng-container *ngIf="desk.key">
            <h2 class="desks-header">{{desk.key}} <span class="count-users">{{desk.value.length}}</span></h2>

            <div class="user" *ngFor="let user of desk.value" [scrollTarget]="'user-' + user.Userid.value">
              <a [href]="'/ga2/#/userprofile?userId=' + user.Userid.value" target="_blank">
                <picture>
                  <i *ngIf="!user.Pictureid" class="fa fa-user"></i>
                  <img *ngIf="user.Pictureid" [src]="profilePictureUrls[user.Pictureid] | safe: 'url'" />
                </picture>
              </a>
              <div class="desk-content">
                <h3 class="title">{{user.Defname}} {{user.Delname}}</h3>
                <p>
                  {{desk.key}}<span *ngIf="user.Ismanager"> MANAGER</span>, {{user.Country}}
                  <span *ngIf="user.handleEntitiesText" [innerHTML]="user.handleEntitiesText | safe: 'html'"></span>
                </p>

                <p>Local Time: {{user.Localtime}}</p>

                <p>
                  <a class="contact" [href]="user.Teams.properties.href | safe: 'url'" target="_blank">Teams</a>
                  <a class="contact" [href]="user.Email.properties.href | safe: 'url'" target="_blank"><i class="fa fa-envelope-o"></i> Email</a>
                </p>
              </div>
            </div>
          </ng-container>
        </section>
      </div>
    </div>
  `,
  styleUrls: ['./desk-overview.scss'],
  encapsulation: ViewEncapsulation.None
})
export class DeskOverviewComponent implements OnInit, OnDestroy, UiOnParamsChanged {
  @Input() public userId:number;

  public loading:boolean;
  public desks:IObject;
  public profilePictureUrls:string[];

  private metadata:IReportMetadata;
  private timeInterval:any;

  constructor(
    private reportDataService:ReportDataService,
    private reportFiltersUtil:ReportFiltersUtil,
    private translationService:TranslationService,
    private deskOverviewDataService:DeskOverviewDataService,
    private scrollService:ScrollService
  ) {
    /**/
  }

  public ngOnInit():void {
    this.loading = true;
    this.loadMetaData()
      .then(this.loadData)
      .then(this.prepareData)
      .finally(() => {
        this.loading = false;

        // Add a timeout to allow the DOM to finish rendering
        setTimeout(() => {
          // scroll to the user ID
          if (this.userId) {
            this.scrollToUser(this.userId);
          }
        });
      });
  }

  public ngOnDestroy():void {
    // cleanup interval of localtime per minute
    clearInterval(this.timeInterval);
  }

  public uiOnParamsChanged(newParams:IObject, transition:Transition):void {
    this.scrollToUser(transition.params('to').userId);
  }

  private loadMetaData():Promise<IReportMetadata> {
    return this.reportDataService.loadMetaData(REPORT_ID)
      .then((metadata:IReportMetadata) => {
        this.metadata = metadata;
        return metadata;
      });
  }

  private loadData = ():Promise<IReportData | string> => {
    const queryFilters = this.reportFiltersUtil.getQueryFiltersFromModel({}, this.metadata, {
      applyDefaultValues: false,
      applyNullValues: true
    });

    return this.reportDataService.loadData(REPORT_ID.toString(), REPORT_ID, queryFilters, {
      average: false,
      min: false,
      max: false,
      count: false,
      sum: false
    }, this.metadata.columns)
      .then((response:IReportResponse) => {
        return response.data;
      });
  };

  private prepareData = (data:IReportData):Promise<string[]> => {
    const filteredUsers:IReportRow[] = _.filter(data?.data, (user:any) => user.Desk);

    const pictureIds:number[] = [];
    _.each(filteredUsers, (user:IReportRow) => {
      // make a list of picture ids
      if (user.Pictureid) {
        pictureIds.push(parseInt(<string>user.Pictureid));
      }

      // add text
      if ((<IReportValue>user.Countofcountries).value || (<IReportValue>user.Countofbp).value) {
        const odLinks:string[] = [];
        if ((<IReportValue>user.Countofcountries).value) {
          odLinks.push(`<a href="${(<IReportValue>user.Countofcountries).properties.href}" target="_blank">${(<IReportValue>user.Countofcountries).value} ${(<IReportValue>user.Countofcountries).value > 1? this.translationService.instant('GA.GENERAL.COUNTRIES') : this.translationService.instant('GA.GENERAL.COUNTRY')}</a>`);
        }
        if ((<IReportValue>user.Countofbp).value) {
          odLinks.push(`<a href="${(<IReportValue>user.Countofbp).properties.href}" target="_blank">${(<IReportValue>user.Countofbp).value} BP${(<IReportValue>user.Countofbp).value > 1? 's' : ''}</a>`);
        }
        user.handleEntitiesText = `<br />PSS ${this.translationService.instant('GA.GENERAL.FOR')} ${odLinks.join(` ${this.translationService.instant('GA.GENERAL.AND')} `)}`;
      } else {
        if ((<IReportValue>user.Countofwebshops).value) {
          user.handleEntitiesText = `<br />GBDM ${this.translationService.instant('GA.GENERAL.FOR')} <a href="${(<IReportValue>user.Countofwebshops).properties.href}" target="_blank">${(<IReportValue>user.Countofwebshops).value} ${(<IReportValue>user.Countofwebshops).value > 1? this.translationService.instant('GA.GENERAL.WEBSHOPS') : 
            this.translationService.instant('GA.GENERAL.WEBSHOP')}</a>`;
        }
      }
    });

    this.desks = _.groupBy(_.sortBy(filteredUsers, 'Desk'), 'Desk');

    return this.deskOverviewDataService.getPictures(pictureIds)
      .then((profilePictureUrls:string[]) => {
        this.profilePictureUrls = _.clone(profilePictureUrls);
        return profilePictureUrls;
      })
      .finally(() => {
        // invoke tickingLocaltimeByMinute() after everything has been loaded
        this.tickingLocaltimeByMinute();
      });
  };

  private tickingLocaltimeByMinute():void {
    // if statements make sure interval will not dupe more than one
    if (this.timeInterval) {
      return;
    }

    this.timeInterval = setInterval(() => {
      this.desks = _.each(this.desks, (item:IObject) => {
        return _.each(item, (data:IObject) => {
          const _tz:string = data.Localtime?.split('T')[ 1 ];
          const timezone:string = _tz?.replace(')', '');
          const timezoneText:string = data.Localtime.substr(6);
          const updatedTime:string = moment().utcOffset(`${timezone}00`).format('HH:mm');

          data.Localtime = `${updatedTime} ${timezoneText}`;

          return data;
        });
      });
    }, 60000);
  }

  private scrollToUser(userId:number):void {
    this.scrollService.scrollTo(`user-${userId}`);
  }
}